import { zodResolver } from "@hookform/resolvers/zod";
import { ISimpleEventDetail, ISubTicketForm, ISubTicketItem, Phase, PriceType, SupportedLanguage } from "interfaces";
import { Controller, useForm } from "react-hook-form";
import { SubTicketSchema } from "../schema";
import { HttpError } from "@refinedev/core";
import { LANGUAGES, PRICE_TYPES } from "utills/constants";
import { get } from "lodash";
import { Label } from "components/label";
import Select from "components/select";
import Input from "components/input";
import { TextEditor } from "components/textEditor";
import { SwitchButton } from "components/switchButton";
import { RightSideModal } from "components/rightSideModal";
import Button from "components/button";
import { IModalProps } from "components/modal";
import { useEffect } from "react";
import moment from "moment";
import DateTimePicker from "components/datetimePicker";

interface SubTicketFormProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  ticketPhases: Phase[];
  onSubmit: (data: ISubTicketForm) => void;
  defaultValues?: ISubTicketItem | null;
}
const DEFAULT_TICKET_VALUE = {
  type_name: {
    en: "",
    vi: ""
  },
  short_description: {
    en: "",
    vi: ""
  },
  price_type: PriceType.PAID,
  original_price: 0,
  price: 0,
  sell_from: "",
  sell_to: "",
  is_soldout: false,
  is_active: true,
  ticket_phase: "",
  hide_in_ui: false
}

const getDefaultTemplate = (defaultValues: SubTicketFormProps["defaultValues"] ) => {
  return {
    type_name: defaultValues?.type_name ?? {
      en: "",
      vi: ""
    },
    short_description: defaultValues?.short_description ?? {
      en: "",
      vi: ""
    },
    price_type: defaultValues?.price_type ?? PriceType.PAID,
    original_price: defaultValues?.original_price ?? 0,
    price: defaultValues?.price ?? 0,
    sell_from: defaultValues
      ? defaultValues?.sell_from
        ? moment(defaultValues.sell_from).format("YYYY-MM-DD HH:mm:ss")
        : ""
      : moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    sell_to: defaultValues
      ? defaultValues?.sell_to
        ? moment(defaultValues.sell_to).format("YYYY-MM-DD HH:mm:ss")
        : ""
      : moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    is_soldout: defaultValues?.is_soldout ?? false,
    is_active: defaultValues?.is_active ?? true,
    ticket_phase: defaultValues?.ticket_phase ?? "",
    hide_in_ui: defaultValues?.hide_in_ui ?? false,
    total_for_sell: defaultValues?.total_for_sell ?? null,
  }
}

export const SubTicketForm = ({title, open, onClose, eventDetailData, ticketPhases, defaultValues, onSubmit}:SubTicketFormProps) => {
  const {
    control,
    handleSubmit,
    register,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ISubTicketForm, HttpError, ISubTicketForm>({
    defaultValues: getDefaultTemplate(defaultValues),
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        SubTicketSchema(eventDetailData.languages)
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultTemplate(defaultValues));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])


  const _submit = (data: ISubTicketForm) => {
    onSubmit(data);
  }


  const firstLanguage = eventDetailData.languages.includes(SupportedLanguage.EN) ? "en" : eventDetailData.languages[0] ?? 'vi';

  const ticketPhaseArray = ticketPhases.map(phase => ({
    label: get(phase, `name.${firstLanguage}`, ""),
    value: get(phase, "id", "")
  }))

  return (
    <RightSideModal title={title ?? ""} open={open} onClose={onClose} footer={(
      <div className="w-full flex flex-row gap-4 items-center">
        <Button 
          className="w-full bg-white border-none text-brand-500 ring-1 ring-inset ring-brand-500 hover:bg-gray-50 active:bg-gray-100" 
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button className="w-full uppercase" onClick={handleSubmit(_submit)}>Save</Button>
      </div>
    )}>
      <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4 p-4 sm:px-6">
        <div className="col-span-full">
          <Controller
            control={control}
            name="price_type"
            render={({ field: { onChange, value = "", ref } }) => {
              return (
                <div className="flex flex-row flex-wrap gap-x-3">
                  {PRICE_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      className={`rounded-md ${type.value === value ? "bg-[#A7B4FE] hover:bg-indigo-300 focus-visible:outline-indigo-300" : "bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50"} px-6 py-3 text-sm font-semibold text-brand-500 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                      onClick={() => {
                        onChange(type.value)
                        if (type.value === PriceType.FREE) {
                          setValue("price", 0, {shouldValidate: true});
                          setValue("original_price", 0, {shouldValidate: true});
                        }
                      }}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              );
            }}
          />
          {get(
            errors,
            `price_type`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `price_type.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>

        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">Sub-ticket Info</p>
          {defaultValues?.id ? (<p className="text-sm mt-1">Product ID: {defaultValues?.id}</p>) : <></>}
        </div>
        {/* <div className="col-span-full flex items-center justify-between font-medium">
          <p className="text-sm">Category</p>
          <p>
            {get(
              ticketData, 
              `category.${firstLanguage}`
            )}
          </p>
        </div> */}

        <div className="col-span-full">
          <Label
            id="ticket_phase"
            name="Ticket Phase"
            required
          />
          <Controller
            control={control}
            name="ticket_phase"
            render={({ field: { onChange, value = "", ref } }) => {
              return (
                <Select
                  key="ticket_phase"
                  inputRef={ref}
                  options={ticketPhaseArray}
                  id="ticket_phase"
                  placeholder="Select ticket phase"
                  defaultValue={
                    ticketPhaseArray.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    ) ?? ""
                  }
                  value={ticketPhaseArray.find(
                    (selected: { label: string; value: string }) =>
                      selected.value === value
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={get(errors, `ticket_phase`, "")}
                />
              );
            }}
          />
          {get(
            errors,
            `ticket_phase`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `ticket_phase.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>

        <div className="col-span-full">
          <Label
            required
            id={`type_name_${eventDetailData.languages[0] ?? "en"}`}
            name="Ticket name displayed"
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div className="relative" key={lang}>
                  <Input
                    id={`type_name_${lang}`}
                    type="text"
                    className="pr-11"
                    placeholder={`Ticket name displayed (${lang})`}
                    {...register(`type_name.${lang}`)}
                    error={get(
                      errors,
                      `type_name.${lang}.message`,
                      ""
                    )}
                  />
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="absolute top-1/2 right-3 -translate-y-1/2"
                  />
                </div>
                {get(
                  errors,
                  `type_name.${lang}`,
                  ""
                ) ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(
                        errors,
                        `type_name.${lang}.message`,
                        ""
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-full">
          <Label
            id={`short_description_${eventDetailData.languages[0] ?? "en"}`}
            name="Note below Ticket name"
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map(
              (lang: SupportedLanguage) => (
                <div key={lang}>
                  <div
                    className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                  >
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="w-4 shrink-0"
                    />
                    <p>
                      {
                        LANGUAGES.find(
                          (language) =>
                            language.value === lang
                        )?.label
                      }
                    </p>
                  </div>

                  <Controller
                    control={control}
                    name={`short_description.${lang}`}
                    render={({
                      field: { onChange, value = "" },
                    }) => (
                      <TextEditor
                        key={`short_description.${lang}`}
                        value={value ?? ""}
                        onChange={onChange}
                      />
                    )}
                  />

                  {get(
                    errors,
                    `short_description.${lang}`,
                    ""
                  ) ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(
                          errors,
                          `short_description.${lang}.message`,
                          ""
                        )}
                      </span>
                    </p>
                  ) : null}
                </div>
              )
            )}
          </div>
        </div>
        <div className="col-span-full">
          <Label
            id="total_for_sell"
            name="Total for sell"
          />
          <Input
            id="total_for_sell"
            type="number"
            min={0}
            placeholder={`Total for sell`}
            {...register(`total_for_sell`, {
              valueAsNumber: true
            })}
            error={get(
              errors,
              `total_for_sell.message`,
              ""
            )}
          />
          {get(
            errors,
            `total_for_sell`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `total_for_sell.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">Price Info</p>
        </div>
        <div className="sm:col-span-3">
          <Label
            id="original_price"
            name="Original price"
            required
          />
          <div className="flex">
            <Input
              id="original_price"
              type="number"
              decimal={2}
              className="rounded-r-none"
              placeholder="Original price"
              disabled={watch("price_type") === PriceType.FREE}
              error={get(errors, "original_price")}
              {...register("original_price", {
                valueAsNumber: true
              })}  
            />
            <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
              {eventDetailData.currency}
            </span>
          </div>
          {get(
            errors,
            `original_price`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  "original_price.message",
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="price"
            name="Sell price"
            required
          />
          <div className="flex">
            <Input
              id="price"
              type="number"
              decimal={2}
              className="rounded-r-none"
              placeholder="Sell price"
              disabled={watch("price_type") === PriceType.FREE}
              error={get(errors, "price")}
              {...register("price", {
                valueAsNumber: true
              })}  
            />
            <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
              {eventDetailData.currency}
            </span>
          </div>
          {get(
            errors,
            `price`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  "price.message",
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="sell_from"
            name="Start date"
            required
          />
          <Controller
            control={control}
            name="sell_from"
            render={({ field: { onChange, value = "" } }) => {
              return (
                <DateTimePicker
                  id="sell_from"
                  placeholder="Start date"
                  error={get(
                    errors,
                    `sell_from.message`,
                    ""
                  )}
                  value={value}
                  onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                />
              );
            }}
          />
          {get(
            errors,
            `sell_from`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `sell_from.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="sell_to"
            name="End date"
            required
          />
          <Controller
            control={control}
            name="sell_to"
            render={({ field: { onChange, value = "" } }) => {
              return (
                <DateTimePicker
                  id="sell_to"
                  placeholder="End date"
                  error={get(
                    errors,
                    `sell_to.message`,
                    ""
                  )}
                  value={value}
                  onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                />
              );
            }}
          />
          {get(
            errors,
            `sell_to`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `sell_to.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>

        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">Advance Settings</p>
        </div>
        <div className="col-span-full flex flex-col items-start gap-y-4">
          <Controller
            control={control}
            name="hide_in_ui"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton label="Show in PROD" checked={!value} onChange={(isChedked) => onChange(!isChedked)} />
            )}
          />
          {get(errors, `hide_in_ui`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `hide_in_ui.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
          <Controller
            control={control}
            name="is_soldout"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton label="Is it Sold Out ?" checked={value} onChange={onChange} />
            )}
          />
          {get(errors, `is_soldout`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `is_soldout.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
      </div>

    </RightSideModal>
  )
}